import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useScrollTo } from "./Navbar";

const NODES = [
  { label: "Enquiries", x: 430, y: 250 },
  { label: "Quotes", x: 384, y: 384 },
  { label: "Jobs", x: 250, y: 430 },
  { label: "Customers", x: 116, y: 384 },
  { label: "Employees", x: 70, y: 250 },
  { label: "Stock", x: 116, y: 116 },
  { label: "Purchasing", x: 250, y: 70 },
  { label: "Reporting", x: 384, y: 116 },
];
const CX = 250;
const CY = 250;

const SystemDiagram = () => (
  <svg viewBox="0 0 500 500" className="w-full h-auto max-w-[520px] mx-auto" data-testid="hero-system-diagram" role="img" aria-label="Diagram of business functions connecting into one central Westline operating system">
    <defs>
      <radialGradient id="centreGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#0D2B1F" stopOpacity="0.10" />
        <stop offset="100%" stopColor="#0D2B1F" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx={CX} cy={CY} r="225" fill="url(#centreGlow)" />
    <circle cx={CX} cy={CY} r="180" fill="none" stroke="#E5E7EB" strokeDasharray="3 6" />
    {NODES.map((n) => (
      <line key={n.label} x1={n.x} y1={n.y} x2={CX} y2={CY} stroke="#C9CFC5" strokeWidth="1" />
    ))}
    {NODES.map((n, i) => (
      <motion.circle
        key={`dot-${n.label}`}
        r="3.5"
        fill="#0D2B1F"
        initial={{ cx: n.x, cy: n.y, opacity: 0 }}
        animate={{ cx: [n.x, CX], cy: [n.y, CY], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.35, ease: "linear" }}
      />
    ))}
    {NODES.map((n, i) => (
      <motion.g
        key={`node-${n.label}`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
      >
        <rect x={n.x - 47} y={n.y - 16} width="94" height="32" rx="6" fill="#FFFFFF" stroke="#DDE2DA" strokeWidth="1" />
        <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="11.5" fontWeight="600" fill="#121413" fontFamily="Inter, sans-serif">
          {n.label}
        </text>
      </motion.g>
    ))}
    <motion.g initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
      <rect x={CX - 78} y={CY - 42} width="156" height="84" rx="10" fill="#0D2B1F" />
      <rect x={CX - 66} y={CY - 28} width="58" height="5" rx="2.5" fill="#739072" opacity="0.9" />
      <rect x={CX - 66} y={CY - 18} width="40" height="5" rx="2.5" fill="#3E5A49" />
      <rect x={CX - 66} y={CY - 6} width="132" height="1" fill="#2A463A" />
      <rect x={CX - 66} y={CY + 4} width="26" height="18" rx="3" fill="#16402E" />
      <rect x={CX - 34} y={CY + 4} width="26" height="18" rx="3" fill="#16402E" />
      <rect x={CX - 2} y={CY + 4} width="26" height="18" rx="3" fill="#16402E" />
      <rect x={CX + 30} y={CY + 4} width="36" height="18" rx="3" fill="#739072" opacity="0.35" />
      <text x={CX} y={CY + 62} textAnchor="middle" fontSize="10.5" letterSpacing="2.5" fontWeight="700" fill="#0D2B1F" fontFamily="Manrope, sans-serif">
        ONE OPERATING SYSTEM
      </text>
    </motion.g>
  </svg>
);

export const Hero = () => {
  const scrollTo = useScrollTo();
  return (
    <section id="home" className="bg-offwhite border-b border-gray-200 scroll-mt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-6"
            data-testid="hero-eyebrow"
          >
            Custom Business Systems &amp; Automation — Australia
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="font-heading font-extrabold tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.06]"
            data-testid="hero-headline"
          >
            Business growth shouldn&rsquo;t create more administration.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-6 text-base md:text-lg text-zinc-600 leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            Westline Technologies designs custom digital operating systems and automation for established businesses.
            We identify repetitive administration, communication and workflow bottlenecks, then build technology
            around the way your business actually operates.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={() => scrollTo("contact")}
              className="inline-flex justify-center items-center gap-2 bg-forest text-white font-semibold text-sm px-7 py-3.5 rounded-md hover:bg-forest-mid transition-colors duration-200"
              data-testid="hero-discuss-button"
            >
              Discuss Your Business
              <ArrowRight size={15} />
            </button>
            <button
              onClick={() => scrollTo("solutions")}
              className="inline-flex justify-center items-center gap-2 border border-charcoal/25 text-charcoal font-semibold text-sm px-7 py-3.5 rounded-md hover:border-forest hover:text-forest transition-colors duration-200"
              data-testid="hero-explore-button"
            >
              Explore What We Build
              <ArrowDown size={15} />
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 pl-4 border-l-2 border-sage text-sm md:text-base font-medium text-charcoal max-w-md"
            data-testid="hero-secondary-statement"
          >
            Increase capacity without increasing management workload at the same rate.
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <SystemDiagram />
        </motion.div>
      </div>
    </section>
  );
};
